type Options = {
  maxChars: number
  isExcluded?: (node: Node) => boolean
}

type Rect = {top: number; left: number; bottom: number; right: number}

const intersects = (a: Rect, b: Rect): boolean =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top

const ROW_SLACK_PX = 6

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE'])

const hiddenByStyle = (el: Element): boolean => {
  const style = getComputedStyle(el)
  return (
    style.visibility === 'hidden' ||
    style.display === 'none' ||
    (style.opacity !== '' && Number(style.opacity) === 0)
  )
}

/**
 * The text a user can see inside `root` right now: text nodes and form field
 * values whose box lies in both the root's box and the viewport (so not what is
 * scrolled away), in DOM order, one line per visual line. SVG text (chart
 * axes and labels) is included. Capped at `maxChars`.
 *
 * This text is page content — titles and comments other users wrote — and goes
 * to the model as data, never as instructions (the backend's tool return says so).
 */
export const collectVisibleText = (root: HTMLElement, {maxChars, isExcluded}: Options): string => {
  const rootRect = root.getBoundingClientRect()
  const view: Rect = {
    top: Math.max(0, rootRect.top),
    left: Math.max(0, rootRect.left),
    bottom: Math.min(window.innerHeight, rootRect.bottom),
    right: Math.min(window.innerWidth, rootRect.right),
  }
  const hiddenCache = new Map<Element, boolean>()
  const isHidden = (el: Element | null): boolean => {
    for (let node = el; node && node !== root.parentElement; node = node.parentElement) {
      const cached = hiddenCache.get(node)
      if (cached !== undefined) return cached
      const hidden = SKIP_TAGS.has(node.tagName) || isExcluded?.(node) || hiddenByStyle(node)
      hiddenCache.set(node, hidden)
      if (hidden) return true
    }
    return false
  }

  const pieces: Array<{text: string; top: number}> = []
  const range = document.createRange()
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT)
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement) {
      if (node instanceof HTMLInputElement && ['password', 'hidden'].includes(node.type)) continue
      const value = node.value.trim()
      if (!value || isHidden(node)) continue
      const rect = node.getBoundingClientRect()
      if (intersects(rect, view)) pieces.push({text: value, top: rect.top})
      continue
    }
    if (node.nodeType !== Node.TEXT_NODE) continue
    const text = node.textContent?.replace(/\s+/g, ' ').trim()
    if (!text || isHidden(node.parentElement)) continue
    range.selectNodeContents(node)
    const rect = range.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0 || !intersects(rect, view)) continue
    pieces.push({text, top: rect.top})
  }

  // DOM order, which keeps a side rail, a chart and a table each in one piece
  // (sorting by position interleaves columns that share a row). Pieces next to
  // each other on the same visual line join with a space.
  let out = ''
  let lineTop: number | null = null
  for (const piece of pieces) {
    const sameLine = lineTop !== null && Math.abs(piece.top - lineTop) < ROW_SLACK_PX
    out += out === '' ? piece.text : `${sameLine ? ' ' : '\n'}${piece.text}`
    lineTop = piece.top
    if (out.length >= maxChars) return out.slice(0, maxChars)
  }
  return out
}
